// src/pages/Dashboard.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmployees, getPosts, getSchedulesRecent, getAppointments, saveSchedules } from '../data/store';

const DIAS = ['Domingo','Lunes','Martes','Miercoles','Jueves','Viernes','Sabado'];
const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

const BASE = new Date(2026, 3, 27);
function fmt(d){ return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; }
function normDate(date){
  if(!date) return '';
  if(date instanceof Date) return fmt(date);
  if(typeof date==='string'){
    if(date.includes('-')){ const [y,m,d]=date.slice(0,10).split('-'); return d+'/'+m+'/'+y; }
    return date;
  }
  return '';
}
function getCurrentOffset(){
  const today=new Date();
  const diff=Math.floor((today-BASE)/(1000*60*60*24));
  return Math.floor(diff/14);
}
function getBisStart(offset){ const m=new Date(BASE); m.setDate(BASE.getDate()+offset*14); return m; }
function getBis(offset){ const mon=getBisStart(offset); return Array.from({length:14},(_,i)=>{ const d=new Date(mon); d.setDate(mon.getDate()+i); return d; }); }
function draftKey(offset){ return 'draft_generated_'+fmt(getBisStart(offset)); }

async function runAutoGenerate(schedules, appointments){
const cur=getCurrentOffset();
const next=cur+1;
const dk=draftKey(next);
if(localStorage.getItem(dk)==='true') return null;
const nextStart=getBisStart(next);
const trigger=new Date(nextStart); trigger.setDate(nextStart.getDate()-1);
const now=new Date();
const isPast = fmt(now)===fmt(trigger) ? now.getHours()>=20 : new Date(now.toDateString())>new Date(trigger.toDateString());
if(!isPast) return null;
const curDays=getBis(cur); const nextDays=getBis(next);
// Build appointment check: each appointment covers a date range
const hasAppt=(empId,isoDate)=>{
return (appointments||[]).some(a=>{
const aId=String(a.employee_id||a.employeeId);
if(aId!==String(empId)) return false;
const aStart=normDate(a.date);
const aEnd=normDate(a.dateEnd||a.date_end||a.date);
const eISO=aEnd||aStart;
return isoDate>=aStart && isoDate<=eISO;
});
};
const drafts=[]; const skipped=[];
nextDays.forEach((day,i)=>{
const curDateStr=normDate(curDays[i]);
const nextDateStr=fmt(day);
const nextISO=nextDateStr;
const prev=schedules.filter(s=>normDate(s.date)===curDateStr&&s.shift&&s.status!=='draft');
prev.forEach(p=>{
const empId=p.employee_id||p.employeeId;
const exists=schedules.some(s=>normDate(s.date)===nextISO&&String(s.employee_id||s.employeeId)===String(empId)&&String(s.post_id||s.postId)===String(p.post_id||p.postId)&&s.shift===p.shift);
if(exists) return;
if(hasAppt(empId,nextISO)){ skipped.push({empId,date:nextISO,postName:p.post_name||''}); return; }
drafts.push({ id:Date.now().toString()+'_'+Math.random().toString(36).substr(2,9), employee_id:empId, employee_name:p.employee_name||null, post_id:p.post_id||p.postId, post_name:p.post_name||null, date:nextISO, shift:p.shift, status:'draft' });
});
});
if(drafts.length>0) await saveSchedules(drafts);
localStorage.setItem(dk,'true');
return { drafts:drafts.length, skipped, nextOffset:next };
}
function getWeekDays(){ const today=new Date(); const day=today.getDay(); const mon=new Date(today); mon.setDate(today.getDate()-(day===0?6:day-1)); return Array.from({length:7},(_,i)=>{ const d=new Date(mon); d.setDate(mon.getDate()+i); return d; }); }
function formatDate(d){ return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; }
function formatShift12h(shift){
  if(!shift) return '';
  if(shift.includes('AM')||shift.includes('PM')) return shift.replace('/',' - ');
  let start='',end='';
  if(shift.includes('/')) [start,end]=shift.split('/');
  else if(shift.includes('-')) [start,end]=shift.split('-');
  else return shift;
  const conv=(t)=>{ if(!t) return ''; let [h,m]=t.split(':'); h=parseInt(h,10); m=m||'00'; const ap=h>=12?'PM':'AM'; h=h%12||12; return h+(m==='00'?'':':'+m)+' '+ap; };
  return conv(start)+' - '+conv(end);
}

export default function Dashboard(){
  const navigate=useNavigate();
  const [employees,setEmployees]=useState([]);
  const [posts,setPosts]=useState([]);
  const [schedules,setSchedules]=useState([]);
  const [appointments,setAppointments]=useState([]);
  const [draftAlert,setDraftAlert]=useState(null);
  const [checked,setChecked]=useState(false);
  const today=new Date();
  const weekDays=getWeekDays();

  const load=useCallback(async()=>{
    try{
      const [e,p,s,a]=await Promise.all([getEmployees(),getPosts(),getSchedulesRecent(),getAppointments()]);
      setEmployees(e||[]); setPosts(p||[]); setSchedules(s||[]); setAppointments(a||[]);
      return {schedules:s||[],appointments:a||[]};
    }catch(err){ console.error(err); return {schedules:[],appointments:[]}; }
  },[]);

  useEffect(()=>{
    load().then(async({schedules:s,appointments:a})=>{
      if(checked) return;
      setChecked(true);
      try{ const result=await runAutoGenerate(s,a); if(result&&result.drafts>0){ setDraftAlert(result); load(); } }
      catch(e){ console.error('AutoGen:',e); }
    });
  },[]);

  // Re-check cada hora
  useEffect(()=>{
    const interval=setInterval(async()=>{
      const [s,a]=await Promise.all([getSchedulesRecent(),getAppointments()]);
      const result=await runAutoGenerate(s,a);
      if(result&&result.drafts>0){ setDraftAlert(result); load(); }
    },60*60*1000);
    return ()=>clearInterval(interval);
  },[]);

  const nextOffset=getCurrentOffset()+1;
  const nextDays=getBis(nextOffset);
  const pendingDrafts=schedules.filter(s=>s.status==='draft'&&nextDays.some(d=>normDate(s.date)===fmt(d)));

  const schedMap={};
  schedules.forEach(s=>{ const empId=s.employee_id||s.employeeId; const key=empId+'_'+s.date; if(!schedMap[key]) schedMap[key]=[]; schedMap[key].push(s); });
  const postMap={};
  posts.forEach(p=>{ postMap[p.id]=p; });

  const totalTurnos=schedules.filter(s=>s.shift).length;
  const turnosHoy=schedules.filter(s=>s.date===formatDate(today)&&s.shift).length;
  const vacantesHoy=posts.reduce((acc,post)=>{ const needed=post.guards_per_shift||post.guardsPerShift||1; const assigned=schedules.filter(s=>String(s.post_id||s.postId)===String(post.id)&&s.date===formatDate(today)&&s.shift).length; return acc+Math.max(0,needed-assigned); },0);
  const nextStart=getBisStart(nextOffset);
  const nextEnd=getBis(nextOffset)[13];

  return(
    <div style={{color:'#fff',maxWidth:1200}}>
      <div style={{marginBottom:'2rem'}}>
        <h1 style={{fontSize:'1.8rem',fontWeight:800,color:'#F5C518',margin:0}}>Dashboard</h1>
        <p style={{color:'#888',marginTop:4,fontSize:15}}>{DIAS[today.getDay()]}, {today.getDate()} de {MESES[today.getMonth()]} de {today.getFullYear()}</p>
      </div>

      {/* Banner: plan recién auto-generado */}
      {draftAlert&&draftAlert.drafts>0&&(
        <div style={{background:'rgba(245,197,24,0.12)',border:'1.5px solid rgba(245,197,24,0.5)',borderRadius:12,padding:'14px 20px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:28}}>{'\uD83D\uDCC5'}</span>
            <div>
              <div style={{color:'#F5C518',fontWeight:700,fontSize:15}}>
                {'\u00A1'}Plan bisemanal auto-generado! ({fmt(getBisStart(draftAlert.nextOffset))} {'-'} {fmt(getBis(draftAlert.nextOffset)[13])})
              </div>
              <div style={{color:'#ccc',fontSize:13,marginTop:2}}>
                {draftAlert.drafts} turnos creados como borrador.
                {draftAlert.skipped&&draftAlert.skipped.length>0&&' '+draftAlert.skipped.length+' turno(s) saltados por cita/vacaciones -> quedan como vacante.'}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>navigate('/horarios')} style={{background:'#F5C518',border:'none',color:'#000',borderRadius:8,padding:'8px 18px',fontWeight:700,cursor:'pointer'}}>Ver y confirmar plan {'→'}</button>
            <button onClick={()=>setDraftAlert(null)} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'#fff',borderRadius:8,padding:'8px 12px',cursor:'pointer'}}>{'\u2715'}</button>
          </div>
        </div>
      )}

      {/* Banner persistente: borradores pendientes */}
      {pendingDrafts.length>0&&!draftAlert&&(
        <div style={{background:'rgba(251,191,36,0.1)',border:'1.5px solid rgba(251,191,36,0.4)',borderRadius:12,padding:'12px 20px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:24}}>{'\u23F3'}</span>
            <div>
              <div style={{color:'#fbbf24',fontWeight:700,fontSize:14}}>Plan pendiente de confirmar ({fmt(nextStart)} {'-'} {fmt(nextEnd)})</div>
              <div style={{color:'#aaa',fontSize:12,marginTop:2}}>{pendingDrafts.length} turno(s) como borrador {'>'} doble click en Horarios para confirmar</div>
            </div>
          </div>
          <button onClick={()=>navigate('/horarios')} style={{background:'#fbbf24',border:'none',color:'#000',borderRadius:8,padding:'8px 16px',fontWeight:700,cursor:'pointer'}}>Ir a Horarios {'→'}</button>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:16,marginBottom:'2rem'}}>
        {[
          {label:'Empleados',value:employees.length,color:'#F5C518'},
          {label:'Puestos activos',value:posts.length,color:'#34d399'},
          {label:'Turnos total',value:totalTurnos,color:'#60a5fa'},
          {label:'Turnos hoy',value:turnosHoy,color:'#f472b6'},
          {label:'Vacantes hoy',value:vacantesHoy,color:vacantesHoy>0?'#f87171':'#34d399'},
          {label:'Borradores prox.',value:pendingDrafts.length,color:pendingDrafts.length>0?'#fbbf24':'#555'},
        ].map(s=>(
          <div key={s.label} style={{background:'#1a1a1a',border:'1px solid #222',borderRadius:12,padding:'1rem 1.25rem'}}>
            <div style={{color:'#666',fontSize:12,marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:28,fontWeight:800,color:s.color}}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{background:'#1a1a1a',border:'1px solid #222',borderRadius:12,padding:'1.5rem'}}>
        <h2 style={{margin:'0 0 1rem',fontSize:16,color:'#F5C518',fontWeight:600}}>Semana actual - {formatDate(weekDays[0])} al {formatDate(weekDays[6])}</h2>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
            <thead>
              <tr>
                <th style={{textAlign:'left',padding:'8px 12px',color:'#666',fontSize:13,borderBottom:'1px solid #333'}}>Empleado</th>
                {weekDays.map(d=>(
                  <th key={formatDate(d)} style={{padding:'8px 6px',color:formatDate(d)===formatDate(today)?'#F5C518':'#666',fontSize:12,borderBottom:'1px solid #333',textAlign:'center',whiteSpace:'nowrap'}}>
                    <div style={{fontWeight:600}}>{DIAS[d.getDay()].slice(0,3)}</div>
                    <div style={{fontSize:11}}>{d.getDate()}/{d.getMonth()+1}</div>
                  </th>
                ))}
                <th style={{padding:'8px',color:'#666',fontSize:12,borderBottom:'1px solid #333',textAlign:'center'}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp=>{
                let total=0;
                return(
                  <tr key={emp.id} style={{borderBottom:'1px solid #1f1f1f'}}>
                    <td style={{padding:'8px 12px',fontSize:13,fontWeight:500,color:'#fff',whiteSpace:'nowrap'}}>
                      <div>{emp.name}</div>
                      <div style={{color:'#666',fontSize:11}}>{emp.position||'Guardia'}</div>
                    </td>
                    {weekDays.map(d=>{
                      const key=emp.id+'_'+formatDate(d);
                      const shifts=schedMap[key]||[];
                      if(shifts.length>0) total+=shifts.length;
                      const isToday=formatDate(d)===formatDate(today);
                      return(
                        <td key={formatDate(d)} style={{padding:'4px',textAlign:'center',background:isToday?'rgba(245,197,24,0.05)':'transparent',verticalAlign:'top'}}>
                          {shifts.length>0?shifts.map((sc,si)=>(
                            <div key={si} style={{fontSize:10,background:'rgba(52,211,153,0.15)',border:'1px solid rgba(52,211,153,0.3)',borderRadius:4,padding:'2px 4px',color:'#34d399',marginBottom:2,whiteSpace:'nowrap'}}>
                              <div style={{fontWeight:600}}>{formatShift12h(sc.shift)}</div>
                              {postMap[sc.post_id||sc.postId]&&<div style={{color:'#6ee7b7',fontSize:9}}>{postMap[sc.post_id||sc.postId].name}</div>}
                            </div>
                          )):<span style={{color:'#333'}}>-</span>}
                        </td>
                      );
                    })}
                    <td style={{padding:'8px',textAlign:'center',color:total>0?'#F5C518':'#333',fontWeight:600,fontSize:13}}>{total>0?total:'-'}</td>
                  </tr>
                );
              })}
              {employees.length===0&&<tr><td colSpan={9} style={{padding:'2rem',textAlign:'center',color:'#444'}}>No hay empleados registrados.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
